import axios from 'axios';
import * as retryAxios from 'retry-axios';
import { getBaseURL, getUserChangedLocale } from '../utils/utils';

const qs = require('query-string');
const req = axios.create();
retryAxios.attach(req);

const apiVersion = 'v1';

const baseURL = getBaseURL(PRODUCTION);

req.defaults.baseURL = baseURL;
req.defaults.withCredentials = true;
req.defaults.headers.common['Content-Type'] = 'application/x-www-form-urlencoded';

req.defaults.xsrfCookieName = 'CSRF-TOKEN';
req.defaults.xsrfHeaderName = 'Z-CSRF-TOKEN';

req.defaults.raxConfig = {
  instance: req,
  shouldRetry: error => {
    if (
      error.config &&
      error.response &&
      error.response.data &&
      error.response.data.error &&
      error.response.data.error[0] &&
      error.response.data.error[0].type === 'api.authorization.bad_csrf_token'
    ) {
      const cfg = retryAxios.getConfig(error);

      if (cfg.currentRetryAttempt <= 5) {
        error.config._isRetry = true;
        return true;
      }
    }

    return false;
  },
};

req.interceptors.request.use(config => {
  if (config._isRetry === true) {
    return config;
  }

  config.data = qs.stringify(config.data);
  return config;
});

req.interceptors.response.use(
  response => ({
    data: response.data.response,
    status: response.status,
    statusText: response.statusText,
    error: response.data.error || '',
  }),
  error => Promise.reject(error),
);
/** Вебмастер */
export const webmaster = {
  login(credentials) {
    return req.post('/login/', credentials).then(res => res.data);
  },

  loginBySocial(social, token) {
    const data = { ...token };
    const lang = getUserChangedLocale();
    if (lang) {
      data.lang = lang;
    }
    return req.post(`/login/${social}/`, data).then(res => res.data);
  },

  activate(hash) {
    return req.post(`/activate/${hash}/`).then(res => res.data);
  },

  logout() {
    return req.post('/logout/').then(res => res.data);
  },

  getBasicInfo() {
    return req.get(`/${apiVersion}/profile/info/`).then(res => res.data);
  },

  setBasicInfo(data, config) {
    return req.put(`/${apiVersion}/profile/info/`, data, config).then(res => res.data);
  },

  getBalance() {
    return req.get(`/${apiVersion}/profile/balance/`).then(res => res.data);
  },

  getHold() {
    return req.get(`/${apiVersion}/profile/hold/`).then(res => res.data);
  },

  getWallets() {
    return req.get(`/${apiVersion}/profile/wallets/`).then(res => res.data);
  },

  getManager() {
    return req.get(`/${apiVersion}/profile/manager/`).then(res => res.data);
  },

  changePassword(passwords) {
    return req.put(`/${apiVersion}/profile/password_change/`, passwords).then(res => res.data);
  },

  getNotify() {
    return req.get(`/${apiVersion}/profile/notify/`).then(res => res.data);
  },

  editNotify(notifications) {
    return req.put(`/${apiVersion}/profile/notify/?${notifications}`).then(res => res.data);
  },

  registration(regData) {
    const data = regData;
    const lang = getUserChangedLocale();
    if (lang) {
      data.lang = lang;
    }
    return req.post('/registration/', data).then(res => res.data);
  },

  passwordRecovery(email, recaptchaToken = '') {
    return req.post(`/recovery/`, { email, recaptchaToken });
  },

  passwordRecoveryNewPassword(data) {
    return req.post(`/recovery/hash/`, { ...data });
  },
  connectAsSlave(hash) {
    return req.post(`/${apiVersion}/profile/slave/${hash}/`).then(res => res.data);
  },
};

/** Площадки */
export const sites = {
  create(site) {
    return req.post(`/${apiVersion}/sites/`, site).then(res => res.data);
  },

  getList(params) {
    return req.get(`/${apiVersion}/sites/`, { params: { ...params } }).then(res => res.data);
  },

  getSite(siteId) {
    return req.get(`/${apiVersion}/sites/${siteId}/`).then(res => res.data);
  },

  update(siteId, updates) {
    return req.put(`/${apiVersion}/sites/${siteId}/`, updates).then(res => res.data);
  },

  delete(siteId) {
    return req.delete(`/${apiVersion}/sites/${siteId}/`).then(res => res.data);
  },

  getSettings() {
    return req.get(`/${apiVersion}/sites/settings/`).then(res => res.data);
  },
};

/** Офферы */
export const offers = {
  getTabs() {
    return req.get(`/${apiVersion}/offers/tabs/`).then(res => res.data);
  },

  getFilters() {
    return req.get(`/${apiVersion}/offers/filters/`).then(res => res.data);
  },

  getSources() {
    return req.get(`/${apiVersion}/offers/sources/`).then(res => res.data);
  },

  getList(params) {
    return req.get(`/${apiVersion}/offers/`, { params: { ...params } }).then(res => res.data);
  },

  getSettings() {
    return req.get(`/${apiVersion}/offers/settings/`).then(res => res.data);
  },

  getOffer(offerId) {
    return req.get(`/${apiVersion}/offers/${offerId}/`).then(res => res.data);
  },

  getOfferInfo(offerId) {
    return req.get(`/${apiVersion}/offers/${offerId}/info/`).then(res => res.data);
  },

  getOfferNews(offerId, params) {
    return req
      .get(`/${apiVersion}/offers/${offerId}/news/`, { params: { ...params } })
      .then(res => res.data);
  },

  getOfferFiles(offerId) {
    return req.get(`/${apiVersion}/offers/${offerId}/files/`).then(res => res.data);
  },

  getOfferPromo(offerId, params) {
    return req
      .get(`/${apiVersion}/offers/${offerId}/news/promo/`, {
        params: { ...params },
      })
      .then(res => res.data);
  },

  getOfferExtraGroups(offerId, params) {
    return req
      .get(`/${apiVersion}/offers/${offerId}/promo/`, {
        params: { ...params },
      })
      .then(res => res.data);
  },

  getOfferExtraCollection(offerId, groupId) {
    return req.get(`/${apiVersion}/offers/${offerId}/promo/${groupId}/`).then(res => res.data);
  },

  searchOffer(params) {
    return req
      .get(`/${apiVersion}/offers/search/`, { params: { ...params } })
      .then(res => res.data);
  },
};

/** Тикеты */
export const tickets = {
  create(ticket) {
    return req.post(`/${apiVersion}/tickets/`, ticket).then(res => res.data);
  },

  getList(params) {
    return req.get(`/${apiVersion}/tickets/`, { params: { ...params } }).then(res => res.data);
  },

  getTicket(ticketId) {
    return req.get(`/${apiVersion}/tickets/${ticketId}/`).then(res => res.data);
  },

  getReplies(ticketId) {
    return req.get(`/${apiVersion}/tickets/${ticketId}/replies/`).then(res => res.data);
  },

  sendReply(ticketId, reply) {
    return req.post(`/${apiVersion}/tickets/${ticketId}/replies/`, reply).then(res => res.data);
  },
};

/** Новости */
export const news = {
  getList(params) {
    return req.get(`/${apiVersion}/news/`, { params: { ...params } }).then(res => res.data);
  },

  getMyNews(params) {
    return req
      .get(`/${apiVersion}/news/`, { params: { type: 'my', ...params } })
      .then(res => res.data);
  },

  getNews(newsId) {
    return req.get(`/${apiVersion}/news/${newsId}/`).then(res => res.data);
  },

  getOfferNews(offerId) {
    return req.get(`/${apiVersion}/news/offer/${offerId}/`).then(res => res.data);
  },

  getPromo(params) {
    return req
      .get(`/${apiVersion}/news/`, { params: { type: 'promo-all', ...params } })
      .then(res => res.data);
  },

  getMyPromo(params) {
    return req
      .get(`/${apiVersion}/news/`, { params: { type: 'promo-my', ...params } })
      .then(res => res.data);
  },
};

/** Настройки */
export const settings = {
  getGlobals() {
    return req.get(`/${apiVersion}/settings/`).then(res => res.data);
  },
  getCountries() {
    return req.get(`/${apiVersion}/geo/country/`).then(res => res.data);
  },
  getSources() {
    return req.get(`/${apiVersion}/offers/sources/`).then(res => res.data);
  },
  getCSRFToken() {
    return req.get(`/csrf/`).then(res => res.data);
  },
};

/** Потоки */
export const streams = {
  getStreams() {
    return req.get(`/${apiVersion}/streams/`).then(res => res.data);
  },
  getStream(streamId) {
    return req.get(`/${apiVersion}/streams/${streamId}/`).then(res => res.data);
  },
};

/** Финансы */
export const finances = {
  getPayments(params) {
    return req
      .get(`/${apiVersion}/profile/finances/balance/`, {
        params: { ...params },
      })
      .then(res => res.data);
  },
};

/** Публичное API */
export const publicRequest = {
  sendFeedBack(params) {
    return req.post(`/public/feedback/`, params).then(res => res.data);
  },

  getNewsList(params) {
    return req.get(`/public/news/`, { params: { ...params } }).then(res => res.data);
  },

  getNewsPromo(params) {
    return req
      .get(`/public/news/`, { params: { type: 'promo-all', ...params } })
      .then(res => res.data);
  },

  getNewsItem(newsId) {
    return req.get(`/public/news/${newsId}/`).then(res => res.data);
  },

  getOffersList(params) {
    return req.get(`/public/offers/`, { params: { ...params } }).then(res => res.data);
  },

  getCountries() {
    return req.get(`/public/geo/country/`).then(res => res.data);
  },

  getTotalInfo() {
    return req.get(`/public/total/info/`).then(res => res.data);
  },

  getSettings() {
    return req.get(`/public/settings/`).then(res => res.data);
  },

  advertiserRegistration(data) {
    return req.post(`/public/a/request/`, data).then(res => res.data);
  },
};

// Менеджер

export const manager = {
  getManager(name) {
    return req.get(`/public/person/${name}/`).then(res => res.data);
  },
};

const ApiService = {
  webmaster,
  sites,
  offers,
  tickets,
  news,
  settings,
  finances,
  publicRequest,
  manager,
};

export default ApiService;
